import type { ReactNode, SVGProps } from "react";

/**
 * Accommodation glyphs, her paths (`l3.js` `UI.P`, admin.js `P`): bed, home,
 * door, check-out, QR, people, swap, undo, download, pin. 24-unit boxes, 1.8
 * stroke, round caps, `currentColor`; sizes come from the caller in rem.
 * Shared ones (zap, chevrons, info, check, alert, clock, plus, pencil, trash,
 * close) are transport's (`components/transport/icons`).
 */
type P = Omit<SVGProps<SVGSVGElement>, "viewBox" | "children">;

function Glyph({ children, ...props }: P & { children: ReactNode }) {
	return (
		<svg
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={1.8}
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden
			focusable={false}
			{...props}
		>
			{children}
		</svg>
	);
}

export const BedIcon = (p: P) => (
	<Glyph {...p}>
		<path d="M3.5 18.5V6M3.5 14h17v4.5M20.5 14v-2.5a3 3 0 0 0-3-3H11V14" />
		<circle cx="7.3" cy="11" r="1.8" />
	</Glyph>
);

export const HomeIcon = (p: P) => (
	<Glyph {...p}>
		<path d="M4 11 12 4.5 20 11" />
		<path d="M6 9.5v10h12v-10" />
		<path d="M10 19.5v-5h4v5" />
	</Glyph>
);

export const DoorIcon = (p: P) => (
	<Glyph {...p}>
		<path d="M6 20.5v-16a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v16M3.5 20.5h17" />
		<path d="M14.5 12v1" />
	</Glyph>
);

export const CheckOutIcon = (p: P) => (
	<Glyph {...p}>
		<path d="M14 4.5h4A1.5 1.5 0 0 1 19.5 6v12a1.5 1.5 0 0 1-1.5 1.5h-4" />
		<path d="M10 16.5 5.5 12 10 7.5M5.5 12H15" />
	</Glyph>
);

export const QrIcon = (p: P) => (
	<Glyph {...p}>
		<rect x="4" y="4" width="6" height="6" rx="1" />
		<rect x="14" y="4" width="6" height="6" rx="1" />
		<rect x="4" y="14" width="6" height="6" rx="1" />
		<path d="M14 14h2.5v2.5H14ZM17.5 17.5H20V20h-2.5ZM20 14v1M14 20h1" />
	</Glyph>
);

export const PeopleIcon = (p: P) => (
	<Glyph {...p}>
		<circle cx="9" cy="8.5" r="3.2" />
		<path d="M3.5 19c.6-3.2 2.8-5 5.5-5s4.9 1.8 5.5 5" />
		<circle cx="16.5" cy="9" r="2.6" />
		<path d="M15.5 14.2c2.6-.3 4.6 1.3 5 4.3" />
	</Glyph>
);

export const SwapIcon = (p: P) => (
	<Glyph {...p}>
		<path d="M7 7h12M15.5 3.5 19 7l-3.5 3.5M17 17H5M8.5 13.5 5 17l3.5 3.5" />
	</Glyph>
);

export const UndoIcon = (p: P) => (
	<Glyph {...p}>
		<path d="M9 7 4.5 11.5 9 16" />
		<path d="M5 11.5h9.5a5 5 0 0 1 0 10H12" />
	</Glyph>
);

export const DownloadIcon = (p: P) => (
	<Glyph {...p}>
		<path d="M12 4.5v11M7.5 11 12 15.5 16.5 11" />
		<path d="M4.5 15v3a1.5 1.5 0 0 0 1.5 1.5h12a1.5 1.5 0 0 0 1.5-1.5v-3" />
	</Glyph>
);

export const PinIcon = (p: P) => (
	<Glyph {...p}>
		<path d="M12 20.5s6.5-5.6 6.5-11a6.5 6.5 0 0 0-13 0c0 5.4 6.5 11 6.5 11Z" />
		<circle cx="12" cy="9.5" r="2.3" />
	</Glyph>
);

export const ListIcon = (p: P) => (
	<Glyph {...p}>
		<path d="M9 7h11M9 12h11M9 17h11M4.5 7h.01M4.5 12h.01M4.5 17h.01" />
	</Glyph>
);

export const CalendarIcon = (p: P) => (
	<Glyph {...p}>
		<rect x="4" y="5.5" width="16" height="14.5" rx="2" />
		<path d="M4 10h16M8.5 3.5v4M15.5 3.5v4" />
	</Glyph>
);

/** The building type glyph: her house for a Games village, a bed otherwise. */
export const BuildingTypeIcon = ({ type, ...p }: P & { type: "VILLAGE" | "HOTEL" | "OTHER" }) =>
	type === "VILLAGE" ? <HomeIcon {...p} /> : <BedIcon {...p} />;
